import { useCallback, useEffect, useRef, useState } from "react";
import type { Session } from "../../gamzone/net/peer.ts";
import { VERSUS_HOST_COST } from "../../gamzone/net/peer.ts";
import { getPoints, spendPoints, usePoints } from "../../gamzone/points.ts";
import { t } from "../../gamzone/i18n.ts";
import { gt } from "../campaign/gameText.ts";
import { AdSlot } from "../../gamzone/ads/AdSlot.tsx";
import type { Disc } from "../othello.ts";
import { Board, Overlay } from "../MatchScreen.tsx";
import { useFriendMatch } from "./useFriendMatch.ts";
import { createFriendNet } from "./net.ts";
import {
  backgroundFor,
  portraitFor,
  type FriendCosmetic,
} from "./cosmetics.ts";

/**
 * 친구랑 같이하기 — a live 1:1 Othello duel over a peer.js Session (see
 * gamzone/net/peer.ts + net.ts). No AI, no stage script: the opponent's
 * moves come from the wire, and the background/top portrait are a random
 * pick from the ending's generic asset pool (no scripted campaign character
 * fits a PvP match) — rolled once by the host and shared via the `start`
 * message so both sides see the same dressing. Ported flow-for-flow from the
 * reference's Coin-puzzle versus mode: host cost, room lobby (Lobby.tsx),
 * loser-requests/winner-accepts rematch.
 */
export function FriendMatch({
  session,
  onExit,
}: {
  session: Session;
  onExit: () => void;
}) {
  const isHost = session.role === "host";
  const localDisc: Disc = isHost ? "dark" : "light";
  const points = usePoints();

  const [cosmetic, setCosmetic] = useState<FriendCosmetic | null>(null);
  const [waitingCopy, setWaitingCopy] = useState(t("waitingForGuest"));
  const [result, setResult] = useState<"win" | "lose" | "draw" | null>(null);
  const [peerLost, setPeerLost] = useState(false);
  const [peerClosed, setPeerClosed] = useState(false);
  const [rematchRequested, setRematchRequested] = useState(false); // I asked
  const [rematchOffered, setRematchOffered] = useState(false); // they asked
  const netRef = useRef<ReturnType<typeof createFriendNet> | null>(null);

  const handleEnd = useCallback((outcome: "dark" | "light" | "draw") => {
    setResult(outcome === "draw" ? "draw" : outcome === localDisc ? "win" : "lose");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const match = useFriendMatch({ localDisc, onEnd: handleEnd });

  useEffect(() => {
    const net = createFriendNet(session, {
      on: {
        onReady: () => {
          if (isHost) setCosmetic(netRef.current!.startMatch());
        },
        onStart: (c) => setCosmetic(c),
        onMove: (cell) => match.receiveMove(cell),
        onRematchOffer: () => setRematchOffered(true),
        onRematchAccept: () => {
          if (!spendPoints(VERSUS_HOST_COST)) return;
          const c = netRef.current!.rematch();
          setCosmetic(c);
          match.reset();
          setResult(null);
          setRematchRequested(false);
          setRematchOffered(false);
        },
        onRematch: (c) => {
          setCosmetic(c);
          match.reset();
          setResult(null);
          setRematchRequested(false);
          setRematchOffered(false);
        },
        onPeerLost: () => setPeerLost(true),
        onPeerClosed: () => setPeerClosed(true),
      },
    });
    netRef.current = net;
    if (!isHost) net.sendReady();
    return () => net.dispose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    if (!peerLost) return undefined;
    const timer = window.setTimeout(() => setWaitingCopy(t("connectFailed")), 4000);
    return () => window.clearTimeout(timer);
  }, [peerLost]);

  const handlePlay = (cell: number) => {
    match.play(cell);
    netRef.current?.sendMove(cell);
  };

  const requestRematch = () => {
    if (getPoints() < VERSUS_HOST_COST) return;
    netRef.current?.sendRematchOffer();
    setRematchRequested(true);
  };
  const acceptRematch = () => {
    netRef.current?.sendRematchAccept();
  };

  const exit = () => {
    netRef.current?.dispose();
    session.close();
    onExit();
  };

  if (!cosmetic) {
    return (
      <div className="bo-screen">
        <section className="bo-card friend-waiting">
          <p className="lobby-status">{peerClosed ? t("connectFailed") : waitingCopy}</p>
          <button className="bo-btn" onClick={exit}>
            {t("exit")}
          </button>
        </section>
      </div>
    );
  }

  const bg = backgroundFor(cosmetic);
  const portrait = portraitFor(cosmetic);
  const view = match.view;
  const myTurn = !view.ended && view.turn === localDisc;
  const turnMsg = view.ended
    ? gt("duelEnd")
    : myTurn
      ? gt("myTurn")
      : gt("opponentTurn");

  return (
    <div className="bo-screen">
      <section className="bo-card" style={{ ["--bo-bg" as string]: `url(${bg})` }}>
        <div className="bo-hero">
          <img className="bo-opp-art" src={portrait} alt="" aria-hidden />
          <div className="bo-topbar">
            <div className="bo-topbar-slot left" />
            <div className="bo-stage">
              <span className="bo-stage-title">{gt("friendMatchTitle")}</span>
              <span className="bo-stage-opp">{`${session.role.toUpperCase()} · ${t("connected")}`}</span>
            </div>
            <div className="bo-topbar-slot right" />
          </div>
        </div>

        <div className="bo-turnbar">
          <span>{peerLost ? waitingCopy : turnMsg}</span>
          <span className="bo-move">
            {gt("moveCounter", { n: String(view.moveNo).padStart(2, "0"), empty: view.emptyCount })}
          </span>
        </div>

        <div className="bo-hud">
          <div className={`bo-side ${view.turn === localDisc ? "is-turn" : ""}`}>
            <span className={`bo-disc-badge ${localDisc}`} />
            <span className="bo-side-label">{gt("me")}</span>
            <span className="bo-side-score">{String(view.playerCount).padStart(2, "0")}</span>
          </div>
          <span className="bo-hud-vs">VS</span>
          <div className={`bo-side right ${view.turn === view.aiDisc ? "is-turn" : ""}`}>
            <span className="bo-side-score">{String(view.aiCount).padStart(2, "0")}</span>
            <span className="bo-side-label">{gt("opponent")}</span>
            <span className={`bo-disc-badge ${view.aiDisc}`} />
          </div>
        </div>

        <Board view={view} hints={false} cursor={-1} onPlay={handlePlay} />

        <footer className="bo-controls">
          <AdSlot />
        </footer>

        {view.ended && result && (
          <Overlay>
            <div className="versus-result">
              <h2 className={`versus-title ${result === "win" ? "vs-win" : result === "lose" ? "vs-lose" : ""}`}>
                {result === "win" ? t("youWin") : result === "lose" ? t("youLose") : gt("draw")}
              </h2>
              <p className="bo-result-score">
                {gt("me")} {view.playerCount} : {view.aiCount} {gt("opponent")}
              </p>
              <div className="versus-actions">
                {result === "win" ? (
                  <button
                    className="versus-btn primary"
                    onClick={acceptRematch}
                    disabled={!rematchOffered}
                  >
                    {t("rematch")}
                  </button>
                ) : rematchRequested ? (
                  <button className="versus-btn" disabled>
                    {t("waitOpponent")}
                  </button>
                ) : (
                  <button
                    className="versus-btn primary"
                    onClick={requestRematch}
                    disabled={points < VERSUS_HOST_COST}
                  >
                    {t("rematchCost", { points: VERSUS_HOST_COST })}
                  </button>
                )}
                <button className="versus-btn" onClick={exit}>
                  {t("exit")}
                </button>
              </div>
              {result !== "win" && points < VERSUS_HOST_COST && (
                <p className="versus-hint">{t("rematchNoPoints")}</p>
              )}
              {result === "win" && !rematchOffered && (
                <p className="versus-hint">{t("waitRematchRequest")}</p>
              )}
            </div>
          </Overlay>
        )}

        {peerClosed && !view.ended && (
          <Overlay>
            <h2 className="bo-result-title">{t("connectFailed")}</h2>
            <div className="bo-result-actions">
              <button className="bo-btn" onClick={exit}>
                {t("exit")}
              </button>
            </div>
          </Overlay>
        )}
      </section>
    </div>
  );
}
